import { safeJson } from "src/app/helpers/Index";
import { faCalendarStar, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { SeasonService } from "src/app/services/Index";
import "./AdminSeasons.css";

type Props = {};
type State = {
	seasons: any[];
	showDialog: boolean;
	edit: any;
	isLoading: boolean;
	saving: boolean;
};

const shortDate = (d: any) => {
	const date = new Date(d);
	return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
};

/**
 * The season a club is in on a given date.
 *
 * 1 June to 31 May — the English football convention, and what Scunthorpe
 * actually run. A July boundary looks tidier but cuts pre-season friendlies out
 * of the season they belong to.
 *
 * Only a default for the pickers: seasons store explicit dates, so a club on a
 * summer calendar isn't fighting a hardcoded assumption.
 */
const suggestSeason = () => {
	const now = new Date();
	const startYear = now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
	return {
		endDate: new Date(startYear + 1, 4, 31),
		name: `${startYear}/${startYear + 1}`,
		startDate: new Date(startYear, 5, 1)
	};
};

/**
 * Seasons. Most of the product hangs off these — fixtures, competitions,
 * season tickets and reporting all belong to one.
 */
export class AdminSeasons extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = { seasons: [], showDialog: false, edit: null, isLoading: true, saving: false };
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const seasons = await safeJson(SeasonService.getAll());
			this.setState({ seasons: Array.isArray(seasons) ? seasons : [], isLoading: false });
		} catch {
			this.setState({ seasons: [], isLoading: false });
		}
	}

	open(season?: any) {
		this.setState({
			edit: season
				? { ...season, endDate: new Date(season.endDate), startDate: new Date(season.startDate) }
				: suggestSeason(),
			showDialog: true
		});
	}

	async save() {
		const s = this.state.edit;
		if (!s?.name?.trim()) return;
		if (new Date(s.endDate) <= new Date(s.startDate)) {
			this.toast.show({ closable: false, detail: "A season has to end after it starts.", life: 4000, severity: "warn", summary: "Check the dates" });
			return;
		}

		this.setState({ saving: true });
		const payload = { endDate: s.endDate, name: s.name.trim(), startDate: s.startDate };
		const res = s.id
			? await SeasonService.updateSingleById(s.id, payload as any)
			: await SeasonService.createSingle(payload as any);
		this.setState({ saving: false });

		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't save" });
			return;
		}
		this.setState({ edit: null, showDialog: false });
		this.load();
	}

	async setCurrent(season: any) {
		const res = await SeasonService.setCurrent(season.id);
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't switch season" });
			return;
		}
		this.toast.show({ closable: false, detail: `New fixtures will go into ${season.name}.`, life: 3000, severity: "success", summary: "Current season changed" });
		this.load();
	}

	async remove(season: any) {
		if (!window.confirm(`Remove ${season.name}? Fixtures played in it keep working — it just stops being offered.`)) return;
		const res = await SeasonService.deleteSingleById(season.id);
		if (!res.ok) {
			const message = await res.text();
			this.toast.show({ closable: false, detail: message || "Please try again later.", life: 5000, severity: "error", summary: "Couldn't remove" });
			return;
		}
		this.load();
	}

	render() {
		const { seasons, isLoading } = this.state;

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 6 }}>Seasons</h2>
				<p style={{ color: "var(--pb-text-muted)", marginBottom: 22 }}>
					Fixtures, competitions and season tickets all belong to a season. New records go into whichever one is current.
				</p>

				<div style={{ marginBottom: 18 }}>
					<Button icon="pi pi-plus" label="Add season" onClick={() => this.open()} />
				</div>

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && seasons.length === 0 &&
					<div className="sn-empty">
						<FontAwesomeIcon icon={faCalendarStar} />
						<h3>No seasons yet</h3>
						<p>Add one and it becomes the season new fixtures go into.</p>
					</div>}

				<div className="sn-grid">
					{seasons.map((season) => (
						<div className={`sn-tile${season.isCurrent ? " current" : ""}`} key={`s-${season.id}`}>
							<div className="sn-tile-head">
								<FontAwesomeIcon icon={faCalendarStar} />
								{season.isCurrent && <span className="sn-badge">CURRENT</span>}
							</div>
							<h4>{season.name}</h4>
							<span className="sn-dates">{shortDate(season.startDate)} &ndash; {shortDate(season.endDate)}</span>
							<div className="sn-actions">
								<Button className="p-button-text" label="Edit" onClick={() => this.open(season)} style={{ padding: "4px 8px" }} />
								{!season.isCurrent &&
									<Button className="p-button-text" label="Make current" onClick={() => this.setCurrent(season)} style={{ padding: "4px 8px" }} />}
								{!season.isCurrent &&
									<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.remove(season)} style={{ padding: "4px 8px" }} />}
							</div>
						</div>
					))}
				</div>

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
				</>} header={this.state.edit?.id ? "Edit season" : "New season"} modal onHide={() => this.setState({ showDialog: false })} style={{ width: 460 }} visible={this.state.showDialog}>
					{this.state.edit && <>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...this.state.edit, name: e.target.value } })} placeholder="2026/2027" style={{ marginBottom: 16, width: "100%" }} value={this.state.edit.name} />

						<div style={{ display: "flex", gap: 12 }}>
							<div style={{ flex: 1 }}>
								<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Starts</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...this.state.edit, startDate: e.value } })} style={{ width: "100%" }} value={this.state.edit.startDate} />
							</div>
							<div style={{ flex: 1 }}>
								<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Ends</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...this.state.edit, endDate: e.value } })} style={{ width: "100%" }} value={this.state.edit.endDate} />
							</div>
						</div>
					</>}
				</Dialog>
			</div>
		);
	}
}
